import { useCallback, useEffect, useState } from 'react';
import { ClockIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useOrganizationStore } from '@/stores/organization';
import { plannerApi } from '@/services/api';

interface MyPresence {
  enabled: boolean;
  geolocation: boolean;
  resource: { _id: string; name?: string } | null;
  last: { type: 'in' | 'out'; date: string; source: string } | null;
  next: 'in' | 'out';
  canVoid: boolean;
}

const VOID_WINDOW_MS = 5 * 60_000;

const timeOf = (iso: string) =>
  new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });

/** Pide la ubicación al navegador; si se deniega o falla, se ficha igual sin ella. */
function readPosition(): Promise<{ latitude: number; longitude: number } | null> {
  if (!('geolocation' in navigator)) return Promise.resolve(null);
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 60_000 }
    );
  });
}

/**
 * Autofichaje (VIDA-256): solo se pinta si la org lo permite y el usuario está
 * ligado a un recurso del planificador. Muestra el estado actual, ficha entrada
 * o salida y permite deshacer el último fichaje durante 5 minutos.
 */
export default function SelfClockButton() {
  const selectedOrgId = useOrganizationStore((s) => s.selectedOrg?._id);
  const plannerOn = useOrganizationStore((s) => s.selectedOrg?.config?.features?.planner === true);
  const [me, setMe] = useState<MyPresence | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await plannerApi.getMyPresence();
      setMe(res.data);
    } catch {
      // Sin planificador o sin acceso: el botón simplemente no aparece.
      setMe(null);
    }
  }, []);

  useEffect(() => {
    setMe(null);
    if (selectedOrgId && plannerOn) void load();
  }, [selectedOrgId, plannerOn, load]);

  // Al acabar la ventana de deshacer, se refresca para ocultar el botón.
  useEffect(() => {
    if (!me?.canVoid || !me.last) return;
    const left = new Date(me.last.date).getTime() + VOID_WINDOW_MS - Date.now();
    const t = setTimeout(() => void load(), Math.max(left, 0) + 1000);
    return () => clearTimeout(t);
  }, [me, load]);

  if (!me?.enabled || !me.resource) return null;

  const clock = async () => {
    setBusy(true);
    try {
      let body: { latitude?: number; longitude?: number; geoDenied?: boolean } = {};
      if (me.geolocation) {
        const pos = await readPosition();
        body = pos ? pos : { geoDenied: true };
      }
      const res = await plannerApi.selfClock(body);
      toast.success(res.data.record.type === 'in' ? 'Entrada registrada' : 'Salida registrada');
      await load();
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(message || 'No se pudo registrar el fichaje');
    } finally {
      setBusy(false);
    }
  };

  const undo = async () => {
    setBusy(true);
    try {
      await plannerApi.selfVoid();
      toast.success('Fichaje anulado');
      await load();
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(message || 'No se pudo anular');
    } finally {
      setBusy(false);
    }
  };

  const inside = me.next === 'out';
  const status = me.last
    ? inside
      ? `Dentro desde ${timeOf(me.last.date)}`
      : `Fuera desde ${timeOf(me.last.date)}`
    : 'Sin fichajes';

  return (
    <div className="flex items-center gap-2">
      <span className="hidden md:inline text-xs text-gray-500">{status}</span>
      {me.canVoid && (
        <button
          type="button"
          onClick={undo}
          disabled={busy}
          className="text-xs font-medium text-gray-500 underline hover:text-gray-800 disabled:opacity-50"
        >
          Deshacer
        </button>
      )}
      <button
        type="button"
        onClick={clock}
        disabled={busy}
        title={status}
        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
          inside ? 'bg-gray-900 text-white hover:bg-gray-800' : 'bg-vida-600 text-white hover:bg-vida-700'
        }`}
      >
        <ClockIcon className="w-4 h-4" aria-hidden="true" />
        <span className="hidden sm:inline">{inside ? 'Fichar salida' : 'Fichar entrada'}</span>
        <span className="sm:hidden">{inside ? 'Salida' : 'Entrada'}</span>
      </button>
    </div>
  );
}
